import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, PencilLine, Plus, Trash2 } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from "@/hooks/useCategories";
import type { CategorieDto } from "@/types/categorie.types";
import { useI18n } from "@/lib/i18n";

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function CategoriesPage() {
  const { t } = useI18n();
  const { data: categories, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [slug, setSlug] = useState("");
  const [libelleFr, setLibelleFr] = useState("");
  const [libelleEn, setLibelleEn] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<CategorieDto | null>(null);

  const categoryList = useMemo(() => Array.isArray(categories) ? categories : [], [categories]);

  const resetForm = () => {
    setEditingId(null);
    setSlug("");
    setLibelleFr("");
    setLibelleEn("");
  };

  const startEdit = (category: CategorieDto) => {
    setEditingId(category.id);
    setSlug(category.slug || "");
    setLibelleFr(category.libelleFr || "");
    setLibelleEn(category.libelleEn || "");
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {
      slug: slug || slugify(libelleFr || libelleEn),
      libelleFr,
      libelleEn,
    };

    if (!payload.slug || !payload.libelleFr || !payload.libelleEn) {
      return;
    }

    const onError = () => toast.error(t("admin.common.saveError"));
    if (editingId) {
      updateCategory.mutate({ id: editingId, payload }, { onSuccess: resetForm, onError });
    } else {
      createCategory.mutate(payload, { onSuccess: resetForm, onError });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteCategory.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => {
        toast.error(t("admin.common.deleteError"));
        setDeleteTarget(null);
      },
    });
  };

  return (
    <>
      <AdminTopbar title={t("admin.settings.categories.title")} subtitle={t("admin.categories.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <ModulePanel title={t("admin.categories.form.title")}>
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Slug</label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t("admin.categories.form.slugPlaceholder")}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.categories.form.labelFr")}</label>
              <input
                value={libelleFr}
                onChange={(e) => setLibelleFr(e.target.value)}
                placeholder="Gouvernance"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.categories.form.labelEn")}</label>
              <input
                value={libelleEn}
                onChange={(e) => setLibelleEn(e.target.value)}
                placeholder="Governance"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <button
                type="submit"
                disabled={createCategory.isPending || updateCategory.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background"
              >
                {(createCategory.isPending || updateCategory.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {editingId ? t("admin.common.save") : t("admin.common.add")}
              </button>
              {editingId ? (
                <button type="button" onClick={resetForm} className="rounded-md border border-border px-4 py-2 text-sm">
                  {t("admin.common.cancel")}
                </button>
              ) : null}
            </div>
          </form>
        </ModulePanel>

        <ModulePanel title={t("admin.categories.list.title", { count: categoryList.length })}>
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("admin.common.loading")}
            </div>
          ) : categoryList.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{t("admin.categories.list.empty")}</div>
          ) : (
            <div className="space-y-2">
              {categoryList.map((category) => (
                <div key={category.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface/50 px-3 py-3">
                  <div>
                    <div className="font-medium text-foreground">{category.libelleFr} / {category.libelleEn}</div>
                    <div className="text-xs text-muted-foreground">slug: {category.slug}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(category)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs"
                    >
                      <PencilLine className="h-3.5 w-3.5" /> {t("admin.common.edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(category)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {t("admin.common.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModulePanel>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.categories.deleteConfirm")}
        onConfirm={confirmDelete}
        loading={deleteCategory.isPending}
      />
    </>
  );
}

export default CategoriesPage;
